import { type PropsWithChildren as PWC, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useDrugProgram } from '../../../contexts/DrugProgramProvider';
import { PrescriberOrderStatus } from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { useMedication } from '../../../hooks/use-medication';
import { usePrescriberOrder } from '../../../hooks/use-prescriber-order';
import {
    getNdcDefinition,
    getNdcDefinitionByNdc,
} from '../../../util/config/get-ndc-definition';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';

import {
    getSwitchTarget,
    useDrugSwitchConfig,
} from './hooks/use-drug-switch-config';
import {
    SwitchPrescriptionContext,
    type SwitchPrescriptionCtx,
} from './shared';

const InternalSwitchPrescriptionProvider = ({ children }: PWC) => {
    const { targetNdc } = useParams();
    const navigate = useNavigate();
    const drugProgram = useDrugProgram();
    const { medication, order, orderId } = usePrescriberOrder();
    const ndcDefinition = getNdcDefinition(drugProgram, medication);
    const drugSwitchConfig = useDrugSwitchConfig(ndcDefinition?.ndc);
    const { medication: switchMedication, loading: medicationLoading } =
        useMedication(targetNdc);
    const switchTargetNdcDefinition = getNdcDefinitionByNdc(
        drugProgram,
        targetNdc
    );

    useEffect(() => {
        if (order.__typename !== 'DigitallyReceivedPrescriberOrder') {
            return;
        }

        if (
            order.status !== PrescriberOrderStatus.OnHold &&
            drugSwitchConfig?.targets.length === 1
        ) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.switch.target.root', {
                    prescriberOrderId: orderId,
                    targetNdc: drugSwitchConfig.targets[0].ndc,
                }),
                { replace: true }
            );
        }
    }, [drugSwitchConfig, order, navigate, orderId]);

    // You can't request a drug switch for an unclaimed order
    if (order.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId: order.drxId ?? '',
                })}
            />
        );
    }

    if (!drugSwitchConfig?.targets.length || !ndcDefinition) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    const switchTarget = getSwitchTarget(targetNdc, drugSwitchConfig);

    const ctx: SwitchPrescriptionCtx = {
        medication: switchMedication,
        medicationLoading,
        ndcDefinition,
        order,
        orderId,
        switchConfig: drugSwitchConfig,
        switchTarget,
        switchTargetNdcDefinition,
    };

    return (
        <SwitchPrescriptionContext.Provider value={ctx}>
            {children}
        </SwitchPrescriptionContext.Provider>
    );
};

export const SwitchPrescriptionProvider = ({ children }: PWC) => {
    const { enableUpdatedDrugSwitchLilly } = useFlags();

    if (enableUpdatedDrugSwitchLilly) {
        return (
            <InternalSwitchPrescriptionProvider>
                {children}
            </InternalSwitchPrescriptionProvider>
        );
    }

    return <>{children}</>;
};
